import { cn } from '@/lib/utils'

interface AppSuspenseFallbackProps {
  className?: string
}

export function AppSuspenseFallback({ className }: AppSuspenseFallbackProps) {
  return (
    <div className={cn('fixed inset-0 z-50 flex items-center justify-center', className)}>
      {/* Layout skeleton */}
      <div className="absolute inset-0 bg-gray-50 dark:bg-gray-900">
        {/* Sidebar skeleton */}
        <div className="absolute left-0 top-0 w-64 h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
          <div className="p-6 space-y-8">
            {/* Logo area */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" />
            </div>

            {/* Navigation items */}
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700">
                <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-18" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-22" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-26" />
              </div>
            </div>

            {/* Section divider */}
            <div className="pt-4">
              <div className="h-px bg-gray-200 dark:bg-gray-700 mb-4" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16 mb-3" />
            </div>

            {/* Secondary navigation */}
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-18" />
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg">
                <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" />
              </div>
            </div>
          </div>

          {/* User section in footer */}
          <div className="mt-auto p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 p-2">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3" />
              </div>
            </div>
          </div>
        </div>

        {/* Main content skeleton */}
        <div className="ml-64 p-8 space-y-8 h-full">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48" />
            </div>
            <div className="flex space-x-3">
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-24" />
              <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-32" />
            </div>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2 w-20" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2 w-24" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2 w-28" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-18" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2 w-22" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-14" />
            </div>
          </div>

          {/* Table skeleton */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32" />
                <div className="flex space-x-2">
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-20" />
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-24" />
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Table header */}
                <div className="grid grid-cols-4 gap-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
                {/* Table rows */}
                {Array.from({ length: 5 }, () => (
                  <div key={Math.random().toString(36)} className="grid grid-cols-4 gap-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" />
                    </div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-12" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Additional content to fill the screen */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4 w-32" />
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-5/6" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-4/5" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4 w-28" />
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-4/5" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-5/6" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3" />
              </div>
            </div>
          </div>

          {/* Bottom section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4 w-40" />
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay with transparent background but blurred */}
      <div className="absolute inset-0 backdrop-blur-md" />

      {/* Centered content */}
      <div className="relative flex flex-col items-center gap-4">
        <img src="/logo.png" alt="Referent" className="h-24 w-auto opacity-80" />
        <div className="h-1 w-64 bg-primary/20 opacity-80 rounded-full overflow-hidden">
          <div className="h-full bg-primary animate-[loading_1.5s_ease-in-out_infinite] rounded-full" />
        </div>
      </div>
    </div>
  )
}
